import React from 'react';

/**
 * Simple in-memory cache for admin panel API calls
 * Reduces redundant API calls without external dependencies
 */

class SimpleCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
  }

  /**
   * Get cached data if valid
   * @param {string} key - Cache key
   * @param {number} maxAge - Max age in milliseconds (default: 5 minutes)
   * @returns {any|null} - Cached data or null if expired/missing
   */
  get(key, maxAge = 5 * 60 * 1000) {
    if (!this.cache.has(key)) {
      return null;
    }

    const timestamp = this.timestamps.get(key);
    const age = Date.now() - timestamp;

    if (age > maxAge) {
      // Expired - remove from cache
      this.cache.delete(key);
      this.timestamps.delete(key);
      return null;
    }

    return this.cache.get(key);
  }

  /**
   * Set cache data
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   */
  set(key, data) {
    this.cache.set(key, data);
    this.timestamps.set(key, Date.now());
  }

  /**
   * Invalidate (clear) specific cache key
   * @param {string} key - Cache key to invalidate
   */
  invalidate(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
  }

  /**
   * Invalidate all cache keys matching a pattern
   * @param {string} pattern - Pattern to match (e.g., 'courses' matches 'courses-list', 'courses-123')
   */
  invalidatePattern(pattern) {
    const keys = Array.from(this.cache.keys());
    keys.forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        this.timestamps.delete(key);
      }
    });
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }

  /**
   * Get cache stats
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      oldestEntry: Math.min(...Array.from(this.timestamps.values())),
      newestEntry: Math.max(...Array.from(this.timestamps.values()))
    };
  }
}

// Export singleton instance
export const cache = new SimpleCache();

/**
 * Custom hook for cached API calls
 * @param {string} key - Cache key
 * @param {Function} fetchFn - Async function to fetch data
 * @param {Object} options - Options { maxAge, enabled }
 * @returns {Object} - { data, loading, error, refetch, invalidate }
 */
export const useCachedFetch = (key, fetchFn, options = {}) => {
  const { maxAge = 5 * 60 * 1000, enabled = true } = options;
  
  const [data, setData] = React.useState(() => {
    // Try to get cached data on mount
    return enabled ? cache.get(key, maxAge) : null;
  });
  
  const [loading, setLoading] = React.useState(!data && enabled);
  const [error, setError] = React.useState(null);

  const fetchData = React.useCallback(async (forceRefresh = false) => {
    if (!enabled) return;

    // Check cache first
    if (!forceRefresh) {
      const cached = cache.get(key, maxAge);
      if (cached) {
        setData(cached);
        setLoading(false);
        return cached;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchFn();
      cache.set(key, result);
      setData(result);
      return result;
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [key, fetchFn, maxAge, enabled]);

  const invalidate = React.useCallback(() => {
    cache.invalidate(key);
    setData(null);
  }, [key]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(true),
    invalidate
  };
};
